import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../hooks/useAdmin';
import { useTranslation } from '../../hooks/useTranslation';
import { AdminPageHeader, AdminStatCard } from '../../components/admin';
import type { Plan, UserRole } from '@flymind/shared';

interface SubscriptionInfo {
  id: string;
  plan: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  createdAt: string;
}

interface UserDetail {
  id: string;
  email: string;
  name: string | null;
  plan: Plan;
  role: UserRole;
  banned: boolean;
  emailVerified: boolean;
  locale: string;
  stripeCustomerId: string | null;
  createdAt: string;
  updatedAt: string;
  subscription: SubscriptionInfo | null;
  monitorCount: number;
  notificationCount: number;
}

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-700',
  premium: 'bg-blue-100 text-blue-700',
  unlimited: 'bg-purple-100 text-purple-700',
};

const ROLE_COLORS: Record<string, string> = {
  user: 'bg-gray-100 text-gray-700',
  admin: 'bg-amber-100 text-amber-700',
  super_admin: 'bg-red-100 text-red-700',
};

export default function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { adminApi, isSuperAdmin } = useAdmin();
  const { t, locale } = useTranslation();
  const dateLocale = locale === 'en' ? 'en-US' : 'it-IT';

  const PLAN_LABELS: Record<string, string> = {
    free: t('admin.common.planFree'),
    premium: t('admin.common.planPremium'),
    unlimited: t('admin.common.planUnlimited'),
  };

  const ROLE_LABELS: Record<string, string> = {
    user: t('admin.common.roleUser'),
    admin: t('admin.common.roleAdmin'),
    super_admin: t('admin.common.roleSuperAdmin'),
  };

  const SUB_STATUS_LABELS: Record<string, string> = {
    active: t('admin.common.subStatusActive'),
    past_due: t('admin.common.subStatusPastDue'),
    canceled: t('admin.common.subStatusCancelled'),
    trialing: t('admin.common.subStatusTrialing'),
    incomplete: t('admin.common.subStatusIncomplete'),
  };

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banLoading, setBanLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [editPlan, setEditPlan] = useState<Plan>('free');
  const [editRole, setEditRole] = useState<UserRole>('user');

  const fetchUser = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.get<UserDetail>(`/users/${id}`);
      setUser(result);
      setEditPlan(result.plan);
      setEditRole(result.role);
    } catch (err) {
      console.error('Errore nel caricamento utente:', err);
      setError(t('admin.users.loadError'));
    } finally {
      setLoading(false);
    }
  }, [adminApi, id, t]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSavePlan = async () => {
    if (!user || editPlan === user.plan) return;
    setSaving(true);
    try {
      await adminApi.patch(`/users/${user.id}`, { plan: editPlan });
      await fetchUser();
    } catch (err) {
      console.error('Errore aggiornamento piano:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (!user || editRole === user.role) return;
    setSaving(true);
    try {
      await adminApi.patch(`/users/${user.id}`, { role: editRole });
      await fetchUser();
    } catch (err) {
      console.error('Errore aggiornamento ruolo:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleBanToggle = async () => {
    if (!user) return;
    if (
      !confirm(
        user.banned
          ? t('admin.users.confirmUnban')
          : t('admin.users.confirmBan'),
      )
    ) {
      return;
    }
    setBanLoading(true);
    try {
      const action = user.banned ? 'unban' : 'ban';
      await adminApi.post(`/users/${user.id}/${action}`);
      await fetchUser();
    } catch (err) {
      console.error('Errore ban/unban:', err);
    } finally {
      setBanLoading(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div>
        <AdminPageHeader title={t('admin.users.detailTitle')} />
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div>
        <AdminPageHeader title={t('admin.users.detailTitle')} />
        <div className="rounded-lg border border-border bg-surface p-8 text-center">
          <p className="text-text-secondary">
            {error || t('admin.users.userNotFound')}
          </p>
          <button
            onClick={() => navigate('/admin/users')}
            className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {t('admin.users.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Back button + header */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/admin/users')}
          className="inline-flex items-center gap-1 text-sm font-medium text-text-secondary hover:text-text-primary"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          {t('admin.users.backToList')}
        </button>
      </div>

      <AdminPageHeader
        title={user.name || user.email}
        subtitle={user.name ? user.email : undefined}
      />

      {/* User profile card */}
      <div className="mb-6 rounded-lg border border-border bg-surface p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">
          {t('admin.users.profileSection')}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldName')}
            </p>
            <p className="mt-1 text-sm text-text-primary">
              {user.name || '-'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldEmail')}
            </p>
            <p className="mt-1 text-sm text-text-primary">{user.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldPlan')}
            </p>
            <p className="mt-1">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PLAN_COLORS[user.plan] || ''}`}
              >
                {PLAN_LABELS[user.plan] || user.plan}
              </span>
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldRole')}
            </p>
            <p className="mt-1">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[user.role] || ''}`}
              >
                {ROLE_LABELS[user.role] || user.role}
              </span>
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldStatus')}
            </p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm">
              <span
                className={`inline-block h-2 w-2 rounded-full ${user.banned ? 'bg-red-500' : 'bg-green-500'}`}
              />
              {user.banned ? t('admin.common.statusBanned') : t('admin.common.statusActive')}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldEmailVerified')}
            </p>
            <p className="mt-1 text-sm text-text-primary">
              {user.emailVerified ? t('admin.common.yesShort') : t('admin.common.noShort')}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldRegistered')}
            </p>
            <p className="mt-1 text-sm text-text-primary">
              {formatDate(user.createdAt)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldUpdated')}
            </p>
            <p className="mt-1 text-sm text-text-primary">
              {formatDate(user.updatedAt)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-text-secondary">
              {t('admin.users.fieldLanguage')}
            </p>
            <p className="mt-1 text-sm text-text-primary">
              {user.locale || '-'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <AdminStatCard
          label={t('admin.users.activeMonitors')}
          value={user.monitorCount}
        />
        <AdminStatCard
          label={t('admin.users.totalNotifications')}
          value={user.notificationCount}
        />
      </div>

      {/* Subscription info */}
      {user.subscription && (
        <div className="mb-6 rounded-lg border border-border bg-surface p-6">
          <h2 className="mb-4 text-lg font-semibold text-text-primary">
            {t('admin.users.subscriptionSection')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary">
                {t('admin.users.fieldPlan')}
              </p>
              <p className="mt-1">
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PLAN_COLORS[user.subscription.plan] || 'bg-gray-100 text-gray-700'}`}
                >
                  {PLAN_LABELS[user.subscription.plan] ||
                    user.subscription.plan}
                </span>
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary">
                {t('admin.users.fieldStatus')}
              </p>
              <p className="mt-1 text-sm text-text-primary">
                {SUB_STATUS_LABELS[user.subscription.status] ||
                  user.subscription.status}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase text-text-secondary">
                {t('admin.users.subPeriod')}
              </p>
              <p className="mt-1 text-sm text-text-primary">
                {formatDate(user.subscription.currentPeriodStart)} -{' '}
                {formatDate(user.subscription.currentPeriodEnd)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions section */}
      <div className="rounded-lg border border-border bg-surface p-6">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">
          {t('admin.users.actionsSection')}
        </h2>
        <div className="space-y-4">
          {/* Change plan */}
          {isSuperAdmin && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="w-32 text-sm font-medium text-text-secondary">
                {t('admin.users.changePlan')}
              </label>
              <select
                value={editPlan}
                onChange={(e) => setEditPlan(e.target.value as Plan)}
                className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="free">{t('admin.users.planFreeOption')}</option>
                <option value="premium">{t('admin.users.planPremiumOption')}</option>
                <option value="unlimited">{t('admin.users.planUnlimitedOption')}</option>
              </select>
              <button
                onClick={handleSavePlan}
                disabled={saving || editPlan === user.plan}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('admin.common.saving') : t('common.save')}
              </button>
            </div>
          )}

          {/* Change role */}
          {isSuperAdmin && (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="w-32 text-sm font-medium text-text-secondary">
                {t('admin.users.changeRole')}
              </label>
              <select
                value={editRole}
                onChange={(e) => setEditRole(e.target.value as UserRole)}
                className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="user">{t('admin.common.roleUser')}</option>
                <option value="admin">{t('admin.common.roleAdmin')}</option>
                <option value="super_admin">{t('admin.common.roleSuperAdmin')}</option>
              </select>
              <button
                onClick={handleSaveRole}
                disabled={saving || editRole === user.role}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? t('admin.common.saving') : t('common.save')}
              </button>
            </div>
          )}

          {/* Ban / Unban */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="w-32 text-sm font-medium text-text-secondary">
              {t('admin.users.userStatus')}
            </label>
            <button
              onClick={handleBanToggle}
              disabled={banLoading}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                user.banned
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {banLoading
                ? t('admin.common.pleaseWait')
                : user.banned
                  ? t('admin.users.unbanUser')
                  : t('admin.users.banUser')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
